import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  DeleteSweep as CleanupIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import useAdminContext from "../../store/contexts/AdminContext";
import SessionList from "./SessionList";
import SessionDetailModal from "./SessionDetailModal";
import { ADMIN_ROLES } from "../../constants/types";

// Inline Terminate Session Dialog Component
const TerminateSessionDialog = ({
  open,
  session,
  onClose,
  onConfirm,
  loading,
}) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError("Please provide a reason for terminating this session");
      return;
    }
    onConfirm(reason);
    setReason("");
    setError("");
  };

  const handleClose = () => {
    setReason("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6">Terminate User Session</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> This will immediately end the user's
            session and force them to log in again. This action cannot be
            undone.
          </Typography>
        </Alert>

        {session && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Session Details:
            </Typography>
            <Typography variant="body2">
              <strong>User:</strong> {session.username || session.user_id}
            </Typography>
            <Typography variant="body2">
              <strong>Device:</strong> {session.device_type || "Unknown"}
            </Typography>
            <Typography variant="body2">
              <strong>IP Address:</strong> {session.ip_address || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Last Active:</strong>{" "}
              {session.last_active
                ? new Date(session.last_active).toLocaleString()
                : "N/A"}
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Reason for Termination *"
          placeholder="Enter the reason for terminating this session"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setError("");
          }}
          error={!!error}
          helperText={error}
          sx={{ mb: 2 }}
        />

        <Typography variant="caption" color="text.secondary">
          * This reason will be logged for audit purposes
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading || !reason.trim()}
        >
          {loading ? "Terminating..." : "Terminate Session"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SessionManagement = () => {
  const {
    admin,
    authState: { loading, error },
    getSessionsListApiCall,
    getSessionByIdApiCall,
    terminateSessionApiCall,
    cleanupExpiredSessionsApiCall,
    clearErrors,
    showSnackbar,
  } = useAdminContext();

  // ============= RBAC HELPER =============
  const isSuperAdmin = () => admin?.role === ADMIN_ROLES.SUPER_ADMIN;

  // State for sessions and pagination
  const [sessions, setSessions] = useState([]);
  const [sessionPagination, setSessionPagination] = useState({
    total: 0,
    pageNo: 1,
    pageSize: 20,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    device_type: "all",
    pageNo: 1,
    pageSize: 20,
  });

  // Modal states
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetailModalOpen, setSessionDetailModalOpen] = useState(false);
  const [terminateSessionDialogOpen, setTerminateSessionDialogOpen] =
    useState(false);

  // Statistics
  const [statistics, setStatistics] = useState({
    totalSessions: 0,
    activeSessions: 0,
    expiredSessions: 0,
    desktopSessions: 0,
    mobileSessions: 0,
  });

  // Load sessions on component mount and filter changes
  useEffect(() => {
    clearErrors();
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pageNo, filters.pageSize]);

  const loadSessions = async (customFilters = filters) => {
    try {
      const result = await getSessionsListApiCall(customFilters);
      if (result) {
        setSessions(result.sessions || []);
        setSessionPagination(result.pagination || {});

        // Calculate statistics
        calculateStatistics(result.sessions || []);
      }
    } catch (error) {
      console.error("Error loading sessions:", error);
    }
  };

  const calculateStatistics = (sessionsList) => {
    const stats = {
      totalSessions: sessionsList.length,
      activeSessions: 0,
      expiredSessions: 0,
      desktopSessions: 0,
      mobileSessions: 0,
    };

    const now = new Date();
    sessionsList.forEach((session) => {
      const expiresAt = new Date(session.expires_at);

      if (expiresAt > now) {
        stats.activeSessions++;
      } else {
        stats.expiredSessions++;
      }

      if (session.device_type === "desktop" || session.device_type === "web") {
        stats.desktopSessions++;
      } else if (
        session.device_type === "mobile" ||
        session.device_type === "tablet"
      ) {
        stats.mobileSessions++;
      }
    });

    setStatistics(stats);
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value,
      pageNo: filterName !== "pageNo" ? 1 : value,
    };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    loadSessions(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: "",
      status: "all",
      device_type: "all",
      pageNo: 1,
      pageSize: 20,
    };
    setFilters(clearedFilters);
    loadSessions(clearedFilters);
  };

  const handleRefresh = () => {
    loadSessions();
  };

  const handlePreviousPage = () => {
    if (sessionPagination.hasPrev) {
      handleFilterChange("pageNo", sessionPagination.pageNo - 1);
    }
  };

  const handleNextPage = () => {
    if (sessionPagination.hasNext) {
      handleFilterChange("pageNo", sessionPagination.pageNo + 1);
    }
  };

  const handleViewSession = async (sessionId) => {
    try {
      const session = await getSessionByIdApiCall(sessionId);
      if (session) {
        setSelectedSession(session);
        setSessionDetailModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching session details:", error);
    }
  };

  // ============= RBAC PROTECTED HANDLERS =============

  const handleTerminateSession = (session) => {
    // RBAC: Only super admins can terminate sessions
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can terminate user sessions", "error");
      return;
    }

    setSelectedSession(session);
    setTerminateSessionDialogOpen(true);
  };

  const handleConfirmTerminateSession = async (reason) => {
    if (!selectedSession) return;

    // RBAC: Double-check super admin status
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can terminate sessions", "error");
      return;
    }

    try {
      const result = await terminateSessionApiCall(selectedSession.id, {
        reason: reason,
      });

      if (result) {
        // Remove the session from the list
        setSessions((prevSessions) =>
          prevSessions.filter((session) => session.id !== selectedSession.id)
        );
        setTerminateSessionDialogOpen(false);
        setSelectedSession(null);
        showSnackbar("Session terminated successfully", "success");
        loadSessions(); // Refresh statistics
      }
    } catch (error) {
      console.error("Error terminating session:", error);
    }
  };

  const handleCleanupExpiredSessions = async () => {
    // RBAC: Only super admins can cleanup expired sessions
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can cleanup expired sessions", "error");
      return;
    }

    try {
      const result = await cleanupExpiredSessionsApiCall();
      if (result) {
        showSnackbar(
          `Successfully cleaned up ${
            result.deletedCount || 0
          } expired sessions`,
          "success"
        );
        loadSessions(); // Refresh the list
      }
    } catch (error) {
      console.error("Error cleaning up expired sessions:", error);
    }
  };

  const handleCloseModals = () => {
    setSessionDetailModalOpen(false);
    setTerminateSessionDialogOpen(false);
    setSelectedSession(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" color="text.primary" gutterBottom>
            Session Management
          </Typography>
          <Typography variant="body1" color="text.primary">
            {isSuperAdmin()
              ? "Monitor active user sessions, terminate suspicious sessions, and manage authentication."
              : "View active user sessions and monitor authentication (Limited termination access)."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          {isSuperAdmin() && (
            <Button
              variant="contained"
              startIcon={<CleanupIcon />}
              onClick={handleCleanupExpiredSessions}
              disabled={loading || statistics.expiredSessions === 0}
              color="warning"
            >
              Cleanup Expired
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* RBAC Warning for Regular Admins */}
      {!isSuperAdmin() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have limited access to session management. Only super admins can
          terminate user sessions or cleanup expired sessions. You can view
          session details and monitor activity.
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="primary" gutterBottom>
              {statistics.totalSessions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Sessions
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="success.main" gutterBottom>
              {statistics.activeSessions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Sessions
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="error.main" gutterBottom>
              {statistics.expiredSessions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expired Sessions
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }} elevation={3}>
            <Typography variant="h6" color="info.main" gutterBottom>
              {statistics.desktopSessions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Desktop Sessions
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by username, IP address..."
              size="small"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="device-type-label">Device Type</InputLabel>
              <Select
                labelId="device-type-label"
                label="Device Type"
                value={filters.device_type}
                onChange={(e) =>
                  handleFilterChange("device_type", e.target.value)
                }
              >
                <MenuItem value="all">All Devices</MenuItem>
                <MenuItem value="web">Web</MenuItem>
                <MenuItem value="mobile">Mobile</MenuItem>
                <MenuItem value="desktop">Desktop</MenuItem>
                <MenuItem value="tablet">Tablet</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                fullWidth
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Session List */}
      <SessionList
        sessions={sessions}
        loading={loading}
        pagination={sessionPagination}
        onViewSession={handleViewSession}
        onTerminateSession={handleTerminateSession}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        isSuperAdmin={isSuperAdmin()}
      />

      {/* Session Detail Modal */}
      <SessionDetailModal
        open={sessionDetailModalOpen}
        session={selectedSession}
        onClose={handleCloseModals}
        onTerminate={handleTerminateSession}
        loading={loading}
        isSuperAdmin={isSuperAdmin()}
      />

      {/* Terminate Session Dialog - Only for Super Admins */}
      {isSuperAdmin() && (
        <TerminateSessionDialog
          open={terminateSessionDialogOpen}
          session={selectedSession}
          onClose={handleCloseModals}
          onConfirm={handleConfirmTerminateSession}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default SessionManagement;
